import { computed, ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { AuthUseCases } from "@/application/use-cases/AuthUseCases";
import { keycloakAuthService } from "@/infrastructure/adapters/KeycloakAuthService";
import { useLocale } from "./useLocale";
import { logger } from "@/infrastructure/services/LoggerService";
// Create singleton instances
const authUseCases = new AuthUseCases(keycloakAuthService);
// Reactive state
const isAuthenticated = ref(false);
const isInitialized = ref(false);
const user = ref(null);
const isLoading = ref(true);
// Token refresh interval
let tokenRefreshInterval = null;
/**
 * Composable for authentication functionality
 * Provides reactive authentication state and methods
 */
export function useAuth() {
    // Initialize locale management
    const { setLocaleFromKeycloak } = useLocale();
    const router = useRouter();
    // Update reactive state
    const updateAuthState = () => {
        const wasInitialized = isInitialized.value;
        isAuthenticated.value = authUseCases.isUserAuthenticated();
        isInitialized.value = authUseCases.isAuthReady();
        const newUser = authUseCases.getCurrentUser();
        user.value = newUser;
        // Handle locale when user info changes
        // Priority: Keycloak (highest) > API > sessionStorage (lowest)
        // Exception: On logout page, preserve user's manual selection
        const isLogoutPage = router.currentRoute.value.path === "/session-ended";
        if (newUser && newUser.locale) {
            if (isLogoutPage) {
                logger.debug(`User authenticated with Keycloak locale: ${newUser.locale}, but on logout page - preserving user's choice`);
            }
            else {
                logger.debug(`User authenticated with Keycloak locale: ${newUser.locale} (highest priority)`);
                setLocaleFromKeycloak(newUser.locale);
            }
        }
        else if (newUser && !newUser.locale) {
            // User authenticated but no locale in Keycloak token
            // Keep the current locale (which could be from API or sessionStorage)
            logger.debug("User authenticated without Keycloak locale, keeping current locale");
            // Don't call initializeLocale() as it might override API-set locale
        }
        // Only set loading to false if we just became initialized or we were already initialized
        if (!wasInitialized && isInitialized.value) {
            isLoading.value = false;
        }
        else if (wasInitialized) {
            isLoading.value = false;
        }
        // If not initialized and wasn't initialized before, keep loading state as is
    };
    // Initialize authentication
    const initialize = async () => {
        try {
            // Don't set loading to false immediately - wait for actual initialization
            await authUseCases.initializeAuth();
            updateAuthState();
            // Start token refresh interval if authenticated
            if (isAuthenticated.value) {
                startTokenRefresh();
            }
        }
        catch (error) {
            logger.debug("Auth initialization failed:", error);
            // Update state even on error so components can handle it
            updateAuthState();
        }
        finally {
            // Always set loading to false after initialization attempt
            isLoading.value = false;
        }
    };
    // Login function
    const login = async (redirectUri, loginHint, organization) => {
        try {
            await authUseCases.loginUser(redirectUri, loginHint, organization);
            updateAuthState();
            if (isAuthenticated.value) {
                startTokenRefresh();
            }
        }
        catch (error) {
            logger.debug("Login failed:", error);
            throw error;
        }
    };
    // Logout function
    const logout = async () => {
        try {
            stopTokenRefresh();
            await authUseCases.logoutUser();
            updateAuthState();
        }
        catch (error) {
            logger.debug("Logout failed:", error);
            throw error;
        }
    };
    // Start token refresh interval
    const startTokenRefresh = () => {
        if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
        }
        tokenRefreshInterval = setInterval(async () => {
            try {
                if (authUseCases.shouldRefreshToken(300)) {
                    // Refresh if expires in 5 minutes
                    await authUseCases.refreshAuthToken();
                    updateAuthState();
                }
            }
            catch (error) {
                logger.debug("Token refresh failed:", error);
                // If refresh fails, user might need to re-login
                updateAuthState();
            }
        }, 60000); // Check every minute
    };
    // Stop token refresh interval
    const stopTokenRefresh = () => {
        if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
            tokenRefreshInterval = null;
        }
    };
    // refresh token manually
    const forceRefreshToken = async () => {
        try {
            await authUseCases.forceRefreshAuthToken();
            updateAuthState();
        }
        catch (error) {
            logger.debug("Manual token refresh failed:", error);
            throw error;
        }
    };
    // Check if user has role
    const hasRole = (role) => {
        return authUseCases.hasUserRole(role);
    };
    // Check if user has any of the roles
    const hasAnyRole = (roles) => {
        return authUseCases.hasAnyUserRole(roles);
    };
    // Get access token
    const getToken = () => {
        return authUseCases.getAccessToken();
    };
    // Get user display name
    const getDisplayName = () => {
        return authUseCases.getUserDisplayName();
    };
    // Get user roles
    const getRoles = () => {
        return authUseCases.getUserRoles();
    };
    // Reinitialize authentication
    const reinitialize = async () => {
        try {
            await authUseCases.reinitializeAuth();
            updateAuthState();
        }
        catch (error) {
            logger.debug("Reinitialize failed:", error);
            throw error;
        }
    };
    // Computed properties
    const userDisplayName = computed(() => getDisplayName());
    const userRoles = computed(() => getRoles());
    const isEmailVerified = computed(() => authUseCases.isUserEmailVerified());
    // Auto-initialize on mount
    onMounted(() => {
        initialize();
    });
    // Cleanup on unmount
    onUnmounted(() => {
        stopTokenRefresh();
    });
    return {
        // State
        isAuthenticated: computed(() => isAuthenticated.value),
        isInitialized: computed(() => isInitialized.value),
        user: computed(() => user.value),
        isLoading: computed(() => isLoading.value),
        userDisplayName,
        userRoles,
        isEmailVerified,
        // Methods
        initialize,
        login,
        logout,
        hasRole,
        hasAnyRole,
        getToken,
        getDisplayName,
        getRoles,
        reinitialize,
        updateAuthState,
        forceRefreshToken,
    };
}
